// Pure proleptic-Gregorian calendar arithmetic on integer day numbers (days since 1970-01-01). No Date, no Intl.
// Algorithms: H. Hinnant, "chrono-Compatible Low-Level Date Algorithms".
import { err, ok, type IsoWeekday, type LocalDate, type LocalTime, type Result } from './types.js';

export const SECONDS_PER_DAY = 86_400;
export const MS_PER_DAY = 86_400_000;

const floorDiv = (a: number, b: number): number => Math.floor(a / b);

export function daysFromCivil(year: number, month: number, day: number): number {
  const y = month <= 2 ? year - 1 : year;
  const era = floorDiv(y, 400);
  const yoe = y - era * 400;
  const mp = (month + 9) % 12;
  const doy = floorDiv(153 * mp + 2, 5) + day - 1;
  const doe = yoe * 365 + floorDiv(yoe, 4) - floorDiv(yoe, 100) + doy;
  return era * 146_097 + doe - 719_468;
}

export function civilFromDays(days: number): { year: number; month: number; day: number } {
  const z = days + 719_468;
  const era = floorDiv(z, 146_097);
  const doe = z - era * 146_097;
  const yoe = floorDiv(doe - floorDiv(doe, 1460) + floorDiv(doe, 36_524) - floorDiv(doe, 146_096), 365);
  const doy = doe - (365 * yoe + floorDiv(yoe, 4) - floorDiv(yoe, 100));
  const mp = floorDiv(5 * doy + 2, 153);
  const day = doy - floorDiv(153 * mp + 2, 5) + 1;
  const month = mp < 10 ? mp + 3 : mp - 9;
  return { year: yoe + era * 400 + (month <= 2 ? 1 : 0), month, day };
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/** ISO weekday of a day number (1970-01-01 was a Thursday). */
export function weekdayOfDays(days: number): IsoWeekday {
  return (((((days + 3) % 7) + 7) % 7) + 1) as IsoWeekday;
}

const pad = (n: number, width: number): string => String(n).padStart(width, '0');

export function formatLocalDate(days: number): LocalDate {
  const { year, month, day } = civilFromDays(days);
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}` as LocalDate;
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{2}):(\d{2})$/;

export function parseLocalDate(text: string): Result<LocalDate, 'invalid_local_date'> {
  const m = DATE_RE.exec(text);
  if (!m) return err('invalid_local_date');
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return err('invalid_local_date');
  return ok(text as LocalDate);
}

export function parseLocalTime(text: string): Result<LocalTime, 'invalid_local_time'> {
  const m = TIME_RE.exec(text);
  if (!m || Number(m[1]) > 23 || Number(m[2]) > 59) return err('invalid_local_time');
  return ok(text as LocalTime);
}

/** Day number of a LocalDate. The brand guarantees the shape; a malformed cast string yields NaN arithmetic. */
export function localDateToDays(date: LocalDate): number {
  return daysFromCivil(Number(date.slice(0, 4)), Number(date.slice(5, 7)), Number(date.slice(8, 10)));
}

export function localTimeToSeconds(time: LocalTime): number {
  return Number(time.slice(0, 2)) * 3600 + Number(time.slice(3, 5)) * 60;
}

export function addDays(date: LocalDate, n: number): LocalDate {
  return formatLocalDate(localDateToDays(date) + n);
}

export function weekdayOf(date: LocalDate): IsoWeekday {
  return weekdayOfDays(localDateToDays(date));
}
